"use client";

import { apiFetch, ApiError, type ApiFetchOptions } from "./client";
import { dataCacheKey } from "./data-cache-identity";
import { rememberResponseVersion } from "./response-version";
import { currentPublicPageToken } from "@/lib/public-page-contract";
import { dataCacheIdentity } from "./data-cache-identity";

type BrowserWarmupEntry = {
  promise?: Promise<unknown>;
  data?: unknown;
  error?: unknown;
  done?: boolean;
  createdAt?: number;
  controller?: AbortController;
  source?: "sidebar" | "fetcher";
  etag?: string | null;
  expiresAt?: number;
  expiryTimer?: ReturnType<typeof setTimeout>;
  cancelled?: boolean;
};

const API_WARMUP_DONE_TTL_MS = 5 * 60 * 1000;
// Только передача уже готового результата первому потребителю. Дальше
// объектом владеет SWR, а очередная ревалидация обязана проверить сервер.
const API_WARMUP_HANDOFF_TTL_MS = 30_000;
const API_WARMUP_UNCLAIMED_LIMIT = 16;

declare global {
  interface Window {
    __frogstatApiWarmup?: Record<string, BrowserWarmupEntry>;
    __frogstatApiWarmupDoneAt?: Record<string, number>;
    __frogstatApiWarmupIdentity?: string;
  }
}

function canUseWarmup(options?: ApiFetchOptions) {
  const method = options?.method?.toUpperCase();
  return (!method || method === "GET") && Object.keys(options ?? {}).every(key =>
    ["method", "previousData", "responseCacheIdentity"].includes(key));
}

function removeEntry(store: Record<string, BrowserWarmupEntry>, key: string, entry: BrowserWarmupEntry): void {
  if (entry.expiryTimer) clearTimeout(entry.expiryTimer);
  if (store[key] === entry) delete store[key];
}

export function isApiWarmupRecentlyDone(url: string, ttlMs = API_WARMUP_DONE_TTL_MS): boolean {
  if (typeof window === "undefined") return false;
  const doneAt = window.__frogstatApiWarmupDoneAt;
  const timestamp = doneAt?.[dataCacheKey(url, window.__frogstatApiWarmupIdentity)];
  return typeof timestamp === "number" && Date.now() - timestamp < ttlMs;
}

/** Смена аккаунта не должна позволить потребить прогрев предыдущего аккаунта. */
export function setApiWarmupIdentity(identity: string): void {
  if (typeof window === "undefined" || window.__frogstatApiWarmupIdentity === identity) return;
  if (window.__frogstatApiWarmupIdentity !== undefined) {
    cancelApiWarmups();
    window.__frogstatApiWarmupDoneAt = {};
  }
  window.__frogstatApiWarmupIdentity = identity;
}

/** The authenticated identity owned by StoreProvider; undefined disables reuse. */
export function getApiWarmupIdentity(): string | undefined {
  const token = currentPublicPageToken();
  if (token) return dataCacheIdentity(`public-page:${token}`, `public-page:${token}`);
  return typeof window === "undefined" ? undefined : window.__frogstatApiWarmupIdentity;
}

function getWarmupStores() {
  if (typeof window === "undefined") return null;
  const store = window.__frogstatApiWarmup = window.__frogstatApiWarmup || {};
  const doneAt = window.__frogstatApiWarmupDoneAt = window.__frogstatApiWarmupDoneAt || {};
  // Таймер может задержаться в фоновой вкладке. Просроченное тело всё равно
  // не должно попасть в первый запрос после её возвращения.
  for (const [key, entry] of Object.entries(store)) {
    if (entry.expiresAt !== undefined && entry.expiresAt <= Date.now()) removeEntry(store, key, entry);
  }
  return { store, doneAt };
}

export function cancelApiWarmups(source?: "sidebar" | "fetcher"): void {
  const stores = getWarmupStores();
  if (!stores) return;
  for (const [url, entry] of Object.entries(stores.store)) {
    if (source && entry.source !== source) continue;
    entry.cancelled = true;
    entry.controller?.abort(new Error("Warmup cancelled"));
    removeEntry(stores.store, url, entry);
  }
}

export function startApiWarmup(url: string, source: "sidebar" | "fetcher" = "sidebar", identity = typeof window !== "undefined" ? window.__frogstatApiWarmupIdentity : undefined): void {
  const stores = getWarmupStores();
  if (!stores) return;
  const key = dataCacheKey(url, identity);
  if (Date.now() - (stores.doneAt[key] ?? 0) < API_WARMUP_DONE_TTL_MS) return;
  if (stores.store[key]) return;
  if (Object.values(stores.store).filter(entry => entry.source === "sidebar").length >= API_WARMUP_UNCLAIMED_LIMIT) return;

  const controller = new AbortController();
  const next: BrowserWarmupEntry = { done: false, createdAt: Date.now(), controller, source };
  next.promise = apiFetch(url, { signal: controller.signal, responseCacheIdentity: identity })
    .then(data => {
      if (next.cancelled) throw new ApiError(0, "Warmup cancelled");
      next.data = data;
      next.done = true;
      stores.doneAt[key] = Date.now();
      // Если таблица ещё не забрала запрос, дать ей забрать готовый объект
      // без второй сети/JSON.parse. После передачи таймер снимается.
      if (stores.store[key] === next && next.source === "sidebar") {
        next.expiresAt = Date.now() + API_WARMUP_HANDOFF_TTL_MS;
        next.expiryTimer = setTimeout(() => removeEntry(stores.store, key, next), API_WARMUP_HANDOFF_TTL_MS);
      }
      return data;
    })
    .catch(error => {
      next.error = error;
      next.done = true;
      throw error;
    })
    .finally(() => {
      if (next.error != null || next.source !== "sidebar") removeEntry(stores.store, key, next);
    });
  stores.store[key] = next;
  void next.promise.catch(() => null);
}

export async function apiFetchWithWarmup<T = unknown>(url: string, options?: ApiFetchOptions, identity?: string): Promise<T> {
  if (typeof window !== "undefined" && canUseWarmup(options)) {
    const stores = getWarmupStores();
    if (!stores) return apiFetch<T>(url, options);
    const { store, doneAt } = stores;
    const cacheIdentity = identity ?? options?.responseCacheIdentity ?? window.__frogstatApiWarmupIdentity;
    const key = dataCacheKey(url, cacheIdentity);
    const entry = store[key];
    // Обновление уже показанных/оптимистически изменённых данных не должно
    // принять подготовку, начатую ДО изменения. SWR объединяет свои refresh.
    if (options?.previousData !== undefined) {
      if (entry?.source === "sidebar") {
        entry.cancelled = true;
        entry.controller?.abort(new Error("Warmup superseded by revalidation"));
        removeEntry(store, key, entry);
      }
      return apiFetch<T>(url, { ...options, responseCacheIdentity: cacheIdentity });
    }
    if (entry) {
      // После передачи запрос уже нужен странице. Отмена следующего
      // перехода не должна оборвать его как ненужную sidebar-подготовку.
      entry.source = "fetcher";
      let data: unknown;
      try {
        data = entry.done ? entry.data : await entry.promise;
        if (entry.cancelled) throw new ApiError(0, "Warmup cancelled");
      } finally {
        removeEntry(store, key, entry);
      }
      if (entry.error == null && data !== undefined) {
        rememberResponseVersion(data, url, entry.etag ?? null);
        doneAt[key] = Date.now();
        return data as T;
      }
    }

    const next: BrowserWarmupEntry = { done: false, createdAt: Date.now(), source: "fetcher" };
    next.promise = apiFetch<T>(url, { ...options, responseCacheIdentity: cacheIdentity })
      .then(data => {
        if (next.cancelled) throw new ApiError(0, "Warmup cancelled");
        next.data = data;
        next.done = true;
        doneAt[key] = Date.now();
        return data;
      })
      .catch(error => {
        next.error = error;
        next.done = true;
        throw error;
      })
      .finally(() => {
        removeEntry(store, key, next);
      });
    store[key] = next;
    return next.promise as Promise<T>;
  }

  return apiFetch<T>(url, { ...options, responseCacheIdentity: identity ?? options?.responseCacheIdentity });
}
